import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Edit, Trash2, UserCog, Shield, User, Search, Grid, List } from 'lucide-react';
import { toast } from 'sonner';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    full_name: '',
    password: '',
    role: 'vendedor',
    is_active: true,
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = users.filter(u => 
        u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [searchQuery, users]);

  // Calculate pagination
  const totalPages = itemsPerPage === 'all' 
    ? 1 
    : Math.ceil(filteredUsers.length / itemsPerPage);
  
  const paginatedUsers = itemsPerPage === 'all' 
    ? filteredUsers 
    : filteredUsers.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
      );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(value === 'all' ? 'all' : parseInt(value));
    setCurrentPage(1);
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/users');
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingUser) {
        // Update user
        const updateData = {
          full_name: formData.full_name,
          email: formData.email,
          role: formData.role,
          is_active: formData.is_active,
        };
        
        // Only include password if it was changed
        if (formData.password) {
          updateData.password = formData.password;
        }

        await axios.put(`/users/${editingUser.id}`, updateData);
        toast.success('Usuario actualizado');
      } else {
        // Create new user
        if (!formData.password) {
          toast.error('La contraseña es requerida');
          setLoading(false);
          return;
        }

        await axios.post('/users', formData);
        toast.success('Usuario creado');
      }

      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      full_name: user.full_name,
      password: '', // Don't show existing password
      role: user.role,
      is_active: user.is_active,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;

    try {
      await axios.delete(`/users/${userId}`);
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      email: '',
      full_name: '',
      password: '',
      role: 'vendedor',
      is_active: true,
    });
    setEditingUser(null);
  };

  return (
    <div className="users-page" data-testid="users-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Gestión de Usuarios</h1>
          <p className="page-subtitle">Administra los usuarios del sistema</p>
        </div>
        <Button onClick={() => { resetForm(); setIsDialogOpen(true); }} data-testid="add-user-btn">
          <Plus size={20} />
          Agregar Usuario
        </Button>
      </div>

      <Card className="search-card">
        <div className="search-controls">
          <div className="search-box">
            <Search size={20} className="search-icon" />
            <Input
              type="text"
              placeholder="Buscar por nombre, usuario o email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
              data-testid="search-users-input"
            />
          </div>
          <div className="view-toggle">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              data-testid="grid-view-btn"
            >
              <Grid size={18} />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              data-testid="list-view-btn"
            >
              <List size={18} />
            </Button>
          </div>
        </div>
      </Card>

      {viewMode === 'grid' ? (
        <div className="users-grid">
        {paginatedUsers.map((user) => (
          <Card key={user.id} className="user-card" data-testid={`user-card-${user.id}`}>
            <div className="user-avatar">
              {user.role === 'admin' ? <Shield size={28} /> : <User size={28} />}
            </div>
            <div className="user-info">
              <h3 className="user-name">{user.full_name}</h3>
              <p className="user-username">@{user.username}</p>
              <p className="user-email">{user.email}</p>
              <div className="user-badges">
                <span className={`role-badge ${user.role === 'admin' ? 'role-admin' : 'role-vendedor'}`}>
                  {user.role === 'admin' ? 'Administrador' : 'Vendedor'}
                </span>
                <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                  {user.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
            <div className="user-actions">
              <Button size="sm" variant="outline" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`}>
                <Edit size={16} />
              </Button>
              <Button size="sm" variant="destructive" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                <Trash2 size={16} />
              </Button>
            </div>
          </Card>
        ))}
        </div>
      ) : (
        <Card className="table-card">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {paginatedUsers.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td><strong>{user.full_name}</strong></td>
                    <td>@{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`role-badge ${user.role === 'admin' ? 'role-admin' : 'role-vendedor'}`}>
                        {user.role === 'admin' ? 'Administrador' : 'Vendedor'}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <Button size="sm" variant="outline" onClick={() => handleEdit(user)}>
                          <Edit size={14} />
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(user.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Pagination Controls */}
      {filteredUsers.length > 0 && (
        <Card className="pagination-card">
          <div className="pagination-controls">
            <div className="items-per-page">
              <Label htmlFor="items-per-page">Elementos por página:</Label>
              <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="items-per-page-select" data-testid="items-per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="all">Todos</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {itemsPerPage !== 'all' && (
              <div className="pagination-info">
                <span>
                  Mostrando {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredUsers.length)} de {filteredUsers.length}
                </span>
              </div>
            )}

            {itemsPerPage !== 'all' && totalPages > 1 && (
              <div className="pagination-buttons">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  data-testid="prev-page-btn"
                >
                  Anterior
                </Button>

                <div className="page-numbers">
                  {[...Array(totalPages)].map((_, index) => {
                    const page = index + 1;
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <Button
                          key={page}
                          variant={currentPage === page ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handlePageChange(page)}
                          data-testid={`page-${page}-btn`}
                        >
                          {page}
                        </Button>
                      );
                    } else if (
                      page === currentPage - 2 ||
                      page === currentPage + 2
                    ) {
                      return <span key={page} className="page-ellipsis">...</span>;
                    }
                    return null;
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  data-testid="next-page-btn"
                >
                  Siguiente
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}

      {filteredUsers.length === 0 && (
        <div className="empty-state">
          <UserCog size={64} />
          <h3>No hay usuarios registrados</h3>
          <p>Agrega tu primer usuario para comenzar</p>
        </div>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="user-dialog">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="user-form">
            <div className="form-group">
              <Label htmlFor="full_name">Nombre Completo*</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                required
                data-testid="user-fullname-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="username">Usuario*</Label>
              <Input
                id="username"
                value={formData.username}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                required
                disabled={!!editingUser}
                data-testid="user-username-input"
              />
              {editingUser && <p className="field-hint">El usuario no puede ser modificado</p>}
            </div>

            <div className="form-group">
              <Label htmlFor="email">Email*</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                data-testid="user-email-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="password">Contraseña{editingUser ? '' : '*'}</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                required={!editingUser}
                data-testid="user-password-input"
              />
              {editingUser && <p className="field-hint">Déjalo vacío para mantener la contraseña actual</p>}
            </div>

            <div className="form-group">
              <Label htmlFor="role">Rol*</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendedor">Vendedor</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-group">
              <Label htmlFor="is_active">Estado*</Label>
              <Select value={formData.is_active.toString()} onValueChange={(value) => setFormData({...formData, is_active: value === 'true'})}>
                <SelectTrigger data-testid="user-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Activo</SelectItem>
                  <SelectItem value="false">Inactivo</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="form-actions">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="save-user-btn">
                {loading ? 'Guardando...' : (editingUser ? 'Actualizar' : 'Guardar')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .users-page {
          max-width: 1400px;
        }

        .user-dialog {
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .page-title {
          font-size: 2rem;
          font-weight: 800;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }

        .page-subtitle {
          color: #64748b;
        }

        .users-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
          gap: 1.5rem;
        }

        .user-card {
          padding: 1.5rem;
          border: none;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          transition: all 0.3s;
        }

        .user-card:hover {
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
          transform: translateY(-2px);
        }

        .user-avatar {
          width: 70px;
          height: 70px;
          border-radius: 12px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1rem;
        }

        .user-info {
          margin-bottom: 1rem;
        }

        .user-name {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .user-username {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.25rem;
          font-weight: 500;
        }

        .user-email {
          font-size: 0.875rem;
          color: #64748b;
          margin-bottom: 0.75rem;
        }

        .user-badges {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .role-badge,
        .status-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 700;
          text-transform: uppercase;
        }

        .role-admin {
          background: #dbeafe;
          color: #1e40af;
        }

        .role-vendedor {
          background: #ede9fe;
          color: #6b21a8;
        }

        .status-active {
          background: #d1fae5;
          color: #065f46;
        }

        .status-inactive {
          background: #fee2e2;
          color: #991b1b;
        }

        .user-actions {
          display: flex;
          gap: 0.5rem;
          padding-top: 1rem;
          border-top: 1px solid #e2e8f0;
        }

        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          color: #94a3b8;
        }

        .empty-state h3 {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 1rem 0 0.5rem;
          color: #64748b;
        }

        .user-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
          margin-top: 1rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .field-hint {
          font-size: 0.75rem;
          color: #64748b;
          margin-top: 0.25rem;
        }

        .form-actions {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          margin-top: 1rem;
        }

        .search-card {
          padding: 1rem;
          margin-bottom: 2rem;
        }

        .search-controls {
          display: flex;
          gap: 1rem;
          align-items: center;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
          flex: 1;
        }

        .search-icon {
          position: absolute;
          left: 1rem;
          color: #64748b;
        }

        .search-input {
          padding-left: 3rem;
        }

        .view-toggle {
          display: flex;
          gap: 0.5rem;
        }

        .table-card {
          padding: 0;
          overflow: hidden;
        }

        .table-container {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table thead {
          background: #f8fafc;
        }

        .data-table th {
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          font-size: 0.875rem;
          color: #64748b;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        .data-table td {
          padding: 1rem;
          border-bottom: 1px solid #e2e8f0;
          font-size: 0.9375rem;
        }

        .data-table tbody tr {
          transition: background 0.2s;
        }

        .data-table tbody tr:hover {
          background: #f8fafc;
        }

        .table-actions {
          display: flex;
          gap: 0.5rem;
        }

        .pagination-card {
          margin-top: 2rem;
          padding: 1.5rem;
        }

        .pagination-controls {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .items-per-page {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .items-per-page-select {
          width: 120px;
        }

        .pagination-info {
          color: #64748b;
          font-size: 0.875rem;
          font-weight: 500;
        }

        .pagination-buttons {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .page-numbers {
          display: flex;
          gap: 0.25rem;
        }

        .page-ellipsis {
          display: flex;
          align-items: center;
          padding: 0 0.5rem;
          color: #64748b;
        }

        @media (max-width: 768px) {
          .page-header {
            flex-direction: column;
          }

          .users-grid {
            grid-template-columns: 1fr;
          }

          .search-controls {
            flex-direction: column;
          }

          .view-toggle {
            width: 100%;
          }

          .view-toggle button {
            flex: 1;
          }

          .pagination-controls {
            flex-direction: column;
            align-items: stretch;
          }

          .items-per-page {
            justify-content: space-between;
          }

          .pagination-buttons {
            flex-direction: column;
            width: 100%;
          }

          .page-numbers {
            justify-content: center;
            flex-wrap: wrap;
          }

          .pagination-info {
            text-align: center;
          }
        }
      `}</style>
    </div>
  );
};

export default Users;
